/**
 * Structural-sharing update primitives for the pure edit catalog.
 *
 * Every helper returns a NEW room in which only the edited node and its
 * ancestors are new objects — all siblings are reference-reused. If the
 * target doesn't exist, or the updater returns the node unchanged
 * (reference-equal), the ORIGINAL room is returned unchanged, so callers can
 * detect no-ops with `===`.
 */
import type { LineEdgeRef } from '../refs';
import {
  type CornerName,
  type EdgeName,
  type FloorPlanCorner,
  type FloorPlanEdgeSegment,
  type FloorPlanFreeElement,
  type FloorPlanPrimaryEdge,
  type FloorPlanRoom,
  roomCorner,
  roomEdge,
} from '../schemas';

/** New array with index i replaced (input array untouched). */
export function replaceAt<T>(arr: readonly T[], i: number, v: T): T[] {
  const out = arr.slice();
  out[i] = v;
  return out;
}

/** A missing edge reads as the empty edge; an edit materializes it. */
export function updateEdge(
  room: FloorPlanRoom,
  edge: EdgeName,
  fn: (e: FloorPlanPrimaryEdge) => FloorPlanPrimaryEdge,
): FloorPlanRoom {
  const cur = roomEdge(room, edge);
  const next = fn(cur);
  return next === cur ? room : { ...room, [edge]: next };
}

/** A missing corner reads as the square corner; an edit materializes it. */
export function updateCorner(
  room: FloorPlanRoom,
  corner: CornerName,
  fn: (c: FloorPlanCorner) => FloorPlanCorner,
): FloorPlanRoom {
  const cur = roomCorner(room, corner);
  const next = fn(cur);
  return next === cur ? room : { ...room, [corner]: next };
}

export function updateFreeElement(
  room: FloorPlanRoom,
  index: number,
  fn: (r: FloorPlanFreeElement) => FloorPlanFreeElement,
): FloorPlanRoom {
  const cur = room.freeElements?.[index];
  if (!cur) return room;
  const next = fn(cur);
  return next === cur ? room : { ...room, freeElements: replaceAt(room.freeElements!, index, next) };
}

/**
 * Update the segment array of ANY line-edge host (pe / aw / rc / ac),
 * rebuilding exactly the path from room to that array. A missing optional
 * host container (an absent alcove sub-wall) is materialized as part of the
 * new structure — the input is never touched. Returns the room unchanged if
 * the host's parent (alcove / chamfer corner) doesn't exist.
 */
export function updateHostSegments(
  room: FloorPlanRoom,
  ref: LineEdgeRef,
  fn: (segments: readonly FloorPlanEdgeSegment[]) => FloorPlanEdgeSegment[] | readonly FloorPlanEdgeSegment[],
): FloorPlanRoom {
  if (ref.k === 'pe') {
    return updateEdge(room, ref.edge, e => {
      const next = fn(e.segments);
      return next === e.segments ? e : { ...e, segments: next as FloorPlanEdgeSegment[] };
    });
  }
  if (ref.k === 'rc') {
    return updateCorner(room, ref.corner, c => {
      const cur = c.segments ?? [];
      const next = fn(cur);
      return next === cur ? c : { ...c, segments: next as FloorPlanEdgeSegment[] };
    });
  }
  // aw / ac: the host lives on an alcove segment of a primary edge.
  const al = room[ref.edge]?.segments[ref.storageIndex];
  if (!al) return room;
  if (ref.k === 'aw') {
    const wallEdge = al[ref.wall];
    const cur = wallEdge?.segments ?? [];
    const next = fn(cur);
    if (next === cur) return room;
    const newAl: FloorPlanEdgeSegment = { ...al, [ref.wall]: { ...(wallEdge ?? {}), segments: next as FloorPlanEdgeSegment[] } };
    return updateEdge(room, ref.edge, e => ({ ...e, segments: replaceAt(e.segments, ref.storageIndex, newAl) }));
  }
  const ch = al[ref.which];
  if (!ch) return room; // no chamfer diagonal to host segments
  const cur = ch.segments ?? [];
  const next = fn(cur);
  if (next === cur) return room;
  const newAl: FloorPlanEdgeSegment = { ...al, [ref.which]: { ...ch, segments: next as FloorPlanEdgeSegment[] } };
  return updateEdge(room, ref.edge, e => ({ ...e, segments: replaceAt(e.segments, ref.storageIndex, newAl) }));
}

/** Update one segment on a line-edge host; room unchanged if it doesn't exist. */
export function updateHostSegment(
  room: FloorPlanRoom,
  ref: LineEdgeRef,
  si: number,
  fn: (seg: FloorPlanEdgeSegment) => FloorPlanEdgeSegment,
): FloorPlanRoom {
  return updateHostSegments(room, ref, segments => {
    const cur = segments[si];
    if (!cur) return segments;
    const next = fn(cur);
    return next === cur ? segments : replaceAt(segments, si, next);
  });
}
